import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { LayoutDashboard, CalendarDays, Receipt, Users, Package, Bot, Menu, X, LogOut, Truck, ClipboardList, Trophy, Clock, TrendingUp, TrendingDown, Wallet, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { InstallApp } from "@/components/InstallApp";

const NAV = [
  { to: "/", label: "Cruscotto", icon: LayoutDashboard, end: true },
  { to: "/eventi", label: "Eventi", icon: CalendarDays },
  { to: "/report", label: "Report Eventi", icon: ClipboardList },
  { to: "/entrate", label: "Entrate", icon: TrendingUp },
  { to: "/uscite", label: "Uscite", icon: TrendingDown },
  { to: "/contabilita", label: "Contabilità", icon: Receipt },
  { to: "/personale", label: "Personale", icon: Users },
  { to: "/accessi", label: "Accessi", icon: ShieldCheck },
  { to: "/timbrature", label: "Timbrature", icon: Clock },
  { to: "/classifica", label: "Classifica", icon: Trophy },
  { to: "/rimborsi", label: "Rimborsi", icon: Wallet },
  { to: "/veicoli", label: "Veicoli", icon: Truck },
  { to: "/magazzino", label: "Magazzino", icon: Package },
  { to: "/socio", label: "Socio AI", icon: Bot, highlight: true },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [rimborsiCount, setRimborsiCount] = useState(0);
  const [vehOverdue, setVehOverdue] = useState(0);
  const [guastiCount, setGuastiCount] = useState(0);
  const loc = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    api.listRimborsi()
      .then((d) => setRimborsiCount((d?.tecnici || []).reduce((n, t) => n + t.voci.filter((v) => !v.paid).length, 0)))
      .catch(() => {});
    api.listVehicles()
      .then((vs) => {
        const today = new Date().toISOString().slice(0, 10);
        setVehOverdue((vs || []).reduce((n, v) => n + (v.deadlines || []).filter((d) => d.date && d.date < today).length, 0));
      })
      .catch(() => {});
    api.listReports()
      .then((rs) => setGuastiCount((rs || []).filter((r) => (r.materiale_guasto || "").trim() || (r.danni_veicolo || "").trim()).length))
      .catch(() => {});
  }, [loc.pathname]);

  const SideContent = () => (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 py-6 border-b border-[#222D42] shrink-0">
        <div className="flex items-center gap-3">
          <img src="/betti-logo.jpg" alt="Betti SRL" className="w-11 h-11 rounded-xl object-cover bg-white shadow-lg" />
          <div>
            <h1 className="font-head font-bold text-lg text-slate-100 leading-none">Betti SRL</h1>
            <p className="text-xs text-slate-500 mt-1 font-mono">Socio Virtuale</p>
          </div>
        </div>
        <div className="mt-4"><InstallApp variant="compact" /></div>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-1" style={{ WebkitOverflowScrolling: "touch" }}>
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? item.highlight
                      ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-900/30"
                      : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                    : "text-slate-400 hover:text-slate-100 hover:bg-[#1A2335]"
                }`
              }
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
              <span className="flex-1">{item.label}</span>
              {item.to === "/rimborsi" && rimborsiCount > 0 && (
                <span data-testid="rimborsi-badge" className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{rimborsiCount}</span>
              )}
              {item.to === "/veicoli" && vehOverdue > 0 && (
                <span data-testid="veicoli-badge" className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{vehOverdue}</span>
              )}
              {item.to === "/report" && guastiCount > 0 && (
                <span data-testid="report-badge" className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{guastiCount}</span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-[#222D42] space-y-3 shrink-0">
        {user && (
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs text-slate-300 truncate font-medium">{user.name || user.email}</p>
              <p className="text-[10px] text-slate-600 font-mono uppercase">{user.role}</p>
            </div>
            <button data-testid="logout-button" onClick={logout} className="text-slate-400 hover:text-red-400 p-1.5 rounded-md hover:bg-red-500/10 transition-colors"><LogOut className="w-4 h-4" /></button>
          </div>
        )}
        <p className="text-xs text-slate-600 font-mono">Noleggio Audio · Video · Luci</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#090D14] text-slate-200 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-[#0d1320] border-r border-[#222D42] sticky top-0 h-screen">
        <SideContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-[#0d1320] border-r border-[#222D42] h-full">
            <button className="absolute top-5 right-4 text-slate-400" onClick={() => setOpen(false)} data-testid="close-menu">
              <X className="w-5 h-5" />
            </button>
            <SideContent />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-[#090D14]/90 backdrop-blur-xl border-b border-[#222D42]">
          <button onClick={() => setOpen(true)} data-testid="open-menu" className="text-slate-300">
            <Menu className="w-6 h-6" />
          </button>
          <span className="flex items-center gap-2"><img src="/betti-logo.jpg" alt="Betti SRL" className="w-7 h-7 rounded-md object-cover bg-white" /><span className="font-head font-bold text-slate-100">Betti SRL</span></span>
          <div className="w-6" />
        </header>
        <main key={loc.pathname} className="flex-1 p-4 md:p-6 lg:p-8 max-w-[1600px] w-full mx-auto animate-fade-up">
          {children}
        </main>
      </div>
    </div>
  );
};
